// Statistics Page - Comprehensive metrics and analytics for Mapas KM
import { createElement } from '@/utils/dom'
import { dashboardApi } from '@/services/api'
import { router } from '@/utils/router'
import { SidebarNavigation } from '@/components/navigation'
import type { DashboardStats } from '@/types'

export class StatisticsPage {
  private stats: DashboardStats | null = null
  private isLoading = true
  private sidebar: SidebarNavigation

  constructor() {
    this.sidebar = new SidebarNavigation({
      currentPage: 'statistics',
      onNavigate: this.handleSidebarNavigation.bind(this)
    })
  }

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'statistics-page app-layout' })

    // Add sidebar
    page.appendChild(this.sidebar.render())

    // Main content area
    const main = createElement('main', { className: 'main-content' })

    if (this.isLoading) {
      main.appendChild(this.createLoadingState())
      this.loadStatisticsData(main)
    } else {
      main.appendChild(this.createStatisticsContent())
    }

    page.appendChild(main)
    return page
  }

  private createLoadingState(): HTMLElement {
    const loader = createElement('div', { className: 'loading-state' })
    loader.innerHTML = `
      <div class="container">
        <div style="text-align: center; padding: 4rem 0;">
          <div class="loading"></div>
          <p style="margin-top: 1rem; color: var(--text-secondary);">A carregar estatísticas...</p>
        </div>
      </div>
    `
    return loader
  }

  private async loadStatisticsData(container: HTMLElement): Promise<void> {
    try {
      // Load statistics data
      const stats = await dashboardApi.getStatistics().catch((error) => {
        console.error('Statistics API failed:', error)
        return null  // Return null instead of mock data for security
      })

      this.stats = stats
      this.isLoading = false

      // Replace loading state with actual content
      container.innerHTML = ''
      container.appendChild(this.createStatisticsContent())

    } catch (error) {
      console.error('Failed to load statistics data:', error)
      // Show empty statistics instead of mock data for security
      this.stats = null
      this.isLoading = false

      container.innerHTML = ''
      container.appendChild(this.createStatisticsContent())
    }
  }



  private createStatisticsContent(): HTMLElement {
    const content = createElement('div', { className: 'statistics-content' })

    if (!this.stats) {
      // Show empty state for new users instead of mock data
      content.innerHTML = `
        <div class="container">
          <div class="statistics-header">
            <div>
              <h1>Estatísticas e Métricas</h1>
              <p style="color: var(--text-secondary);">Análise detalhada dos padrões de deslocação e simulações de rotas empresariais</p>
            </div>
          </div>
          
          <div class="empty-state" style="text-align: center; padding: 4rem 2rem; color: var(--text-secondary);">
            <i class="fas fa-chart-line" style="font-size: 3rem; margin-bottom: 1rem; color: var(--primary-color);"></i>
            <h3>Ainda não existem dados estatísticos</h3>
            <p>Gere a sua primeira simulação de rota para ver estatísticas detalhadas aqui.</p>
            <button class="btn btn-primary" style="margin-top: 1rem;" id="generate-first-route">
              <i class="fas fa-route"></i>
              Gerar Primeira Rota
            </button>
          </div>
        </div>
      `

      // Add event listener for route generation button
      setTimeout(() => {
        const button = content.querySelector('#generate-first-route')
        if (button) {
          button.addEventListener('click', () => this.openRouteGenerationModal())
        }
      }, 0)

      return content
    }

    content.innerHTML = `
      <div class="container">
        <!-- Header -->
        <div class="statistics-header">
          <div>
            <h1>Estatísticas e Métricas</h1>
            <p style="color: var(--text-secondary);">Análise detalhada dos padrões de deslocação e simulações de rotas empresariais</p>
          </div>
        </div>

        <!-- Main Stats Grid -->
        ${this.createMainStatsGrid()}
        
        <!-- Detailed Analytics Section -->
        ${this.createAnalyticsSection()}
        
        <!-- Trends and Comparisons -->
        ${this.createTrendsSection()}
      </div>
    `

    return content
  }

  private createMainStatsGrid(): string {
    if (!this.stats) return ''

    const { current_month, trends } = this.stats

    return `
      <div class="stats-grid stats-main">
        <div class="stat-card featured">
        <div class="stat-header">
        <h3>Total de Simulações</h3>
        <div class="stat-trend ${this.getTrendClass(trends.reports_change)}">
        ${this.formatTrend(trends.reports_change)}
        </div>
        </div>
        <div class="stat-value">${current_month.total_reports}</div>
        <div class="stat-subtitle">Simulações este mês</div>
        <div class="stat-description">
        Total de simulações de rotas geradas e analisadas
        </div>
        </div>

        <div class="stat-card featured">
          <div class="stat-header">
            <h3>Quilómetros Registados</h3>
            <div class="stat-trend ${this.getTrendClass(trends.kilometers_change)}">
              ${this.formatTrend(trends.kilometers_change)}
            </div>
          </div>
          <div class="stat-value">${this.formatKilometers(current_month.total_kilometers)}</div>
          <div class="stat-subtitle">Distância percorrida</div>
          <div class="stat-description">
          Total de quilómetros simulados nas rotas empresariais
          </div>
        </div>

        <div class="stat-card featured">
          <div class="stat-header">
            <h3>Total de Despesas</h3>
            <div class="stat-trend ${this.getTrendClass(trends.cost_change)}">
              ${this.formatTrend(trends.cost_change)}
            </div>
          </div>
          <div class="stat-value">€${this.formatCurrency(current_month.total_cost)}</div>
          <div class="stat-subtitle">Despesas registadas</div>
          <div class="stat-description">
            Valor total de despesas de viagem processadas
          </div>
        </div>

        <div class="stat-card highlight featured">
        <div class="stat-header">
        <h3>Valor Simulado</h3>
        <div class="stat-trend ${this.getTrendClass(trends.savings_change)}">
        ${this.formatTrend(trends.savings_change)}
        </div>
        </div>
        <div class="stat-value savings">€${this.formatCurrency(current_month.tax_savings)}</div>
        <div class="stat-subtitle">Total estimado</div>
        <div class="stat-description">
        Valor total estimado das deslocações simuladas
        </div>
        </div>
      </div>
    `
  }

  private createAnalyticsSection(): string {
    if (!this.stats) return ''

    const { current_month } = this.stats
    const avgKmPerReport = current_month.total_reports > 0 ?
      (current_month.total_kilometers / current_month.total_reports).toFixed(1) : '0'
    const avgCostPerKm = current_month.total_kilometers > 0 ?
      (current_month.total_cost / current_month.total_kilometers).toFixed(3) : '0'
    const savingsRate = current_month.total_cost > 0 ?
      ((current_month.tax_savings / current_month.total_cost) * 100).toFixed(1) : '0'

    return `
      <div class="analytics-section">
        <h2>Análise Detalhada</h2>
        <div class="analytics-grid">
          <div class="analytics-card">
            <div class="analytics-icon">📊</div>
            <div class="analytics-content">
              <h4>Média por Relatório</h4>
              <div class="analytics-value">${avgKmPerReport} km</div>
              <p class="analytics-description">Distância média por relatório gerado</p>
            </div>
          </div>

          <div class="analytics-card">
            <div class="analytics-icon">⛽</div>
            <div class="analytics-content">
              <h4>Custo por Quilómetro</h4>
              <div class="analytics-value">€${avgCostPerKm}</div>
              <p class="analytics-description">Custo médio por quilómetro percorrido</p>
            </div>
          </div>

          <div class="analytics-card">
          <div class="analytics-icon">💰</div>
          <div class="analytics-content">
          <h4>Eficiência de Rotas</h4>
          <div class="analytics-value">${savingsRate}%</div>
          <p class="analytics-description">Percentagem de otimização das rotas simuladas</p>
          </div>
          </div>

          <div class="analytics-card">
          <div class="analytics-icon">📈</div>
          <div class="analytics-content">
          <h4>Qualidade de Simulação</h4>
          <div class="analytics-value">Excelente</div>
          <p class="analytics-description">Classificação baseada no realismo das rotas geradas</p>
          </div>
          </div>
        </div>
      </div>
    `
  }

  private createTrendsSection(): string {
    if (!this.stats) return ''

    const { current_month, previous_month } = this.stats

    return `
      <div class="trends-section">
        <h2>Comparação Mensal</h2>
        <div class="trends-grid">
          <div class="trend-card">
            <h4>Relatórios</h4>
            <div class="trend-comparison">
              <div class="trend-current">
                <span class="trend-label">Este Mês</span>
                <span class="trend-value">${current_month.total_reports}</span>
              </div>
              <div class="trend-previous">
                <span class="trend-label">Mês Anterior</span>
                <span class="trend-value">${previous_month.total_reports}</span>
              </div>
              <div class="trend-change ${this.getTrendClass(this.stats.trends.reports_change)}">
                ${this.formatTrend(this.stats.trends.reports_change)}
              </div>
            </div>
          </div>

          <div class="trend-card">
            <h4>Quilómetros</h4>
            <div class="trend-comparison">
              <div class="trend-current">
                <span class="trend-label">Este Mês</span>
                <span class="trend-value">${this.formatKilometers(current_month.total_kilometers)}</span>
              </div>
              <div class="trend-previous">
                <span class="trend-label">Mês Anterior</span>
                <span class="trend-value">${this.formatKilometers(previous_month.total_kilometers)}</span>
              </div>
              <div class="trend-change ${this.getTrendClass(this.stats.trends.kilometers_change)}">
                ${this.formatTrend(this.stats.trends.kilometers_change)}
              </div>
            </div>
          </div>

          <div class="trend-card">
            <h4>Despesas</h4>
            <div class="trend-comparison">
              <div class="trend-current">
                <span class="trend-label">Este Mês</span>
                <span class="trend-value">€${this.formatCurrency(current_month.total_cost)}</span>
              </div>
              <div class="trend-previous">
                <span class="trend-label">Mês Anterior</span>
                <span class="trend-value">€${this.formatCurrency(previous_month.total_cost)}</span>
              </div>
              <div class="trend-change ${this.getTrendClass(this.stats.trends.cost_change)}">
                ${this.formatTrend(this.stats.trends.cost_change)}
              </div>
            </div>
          </div>

          <div class="trend-card highlight">
          <h4>Valor Estimado</h4>
            <div class="trend-comparison">
              <div class="trend-current">
                <span class="trend-label">Este Mês</span>
                <span class="trend-value">€${this.formatCurrency(current_month.tax_savings)}</span>
              </div>
              <div class="trend-previous">
                <span class="trend-label">Mês Anterior</span>
                <span class="trend-value">€${this.formatCurrency(previous_month.tax_savings)}</span>
              </div>
              <div class="trend-change ${this.getTrendClass(this.stats.trends.savings_change)}">
                ${this.formatTrend(this.stats.trends.savings_change)}
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  private handleSidebarNavigation(page: string): void {
    switch (page) {
      case 'dashboard':
        router.navigate('/dashboard')
        break
      case 'generate':
        // Trigger route generation modal - this is handled by dashboard
        router.navigate('/dashboard')
        // Wait a bit then trigger the modal
        setTimeout(() => {
          const event = new CustomEvent('sidebar:generate-route')
          document.dispatchEvent(event)
        }, 100)
        break
      case 'trip-purposes':
        router.navigate('/trip-purposes')
        break
      case 'whats-new':
        router.navigate('/whats-new')
        break
      case 'settings':
        router.navigate('/settings')
        break
      case 'admin':
        router.navigate('/admin')
        break
    }
  }

  private openRouteGenerationModal(): void {
    // Navigate to dashboard and trigger the route generation modal
    router.navigate('/dashboard')
    // Wait a bit for navigation then trigger the modal
    setTimeout(() => {
      const event = new CustomEvent('sidebar:generate-route')
      document.dispatchEvent(event)
    }, 200)
  }

  // Utility methods
  private formatCurrency(amount: number): string {
    return amount.toLocaleString('pt-PT', { minimumFractionDigits: 2 })
  }

  private formatKilometers(km: number): string {
    return `${km.toLocaleString('pt-PT')} km`
  }

  private formatTrend(trend: string): string {
    const value = parseFloat(trend)
    const symbol = value >= 0 ? '↗' : '↘'
    return `${symbol} ${Math.abs(value).toFixed(1)}%`
  }

  private getTrendClass(trend: string): string {
    const value = parseFloat(trend)
    return value >= 0 ? 'trend-up' : 'trend-down'
  }
}
